// The voice session's one call — T67 (13 Sep 2026): the short-lived key the browser opens its
// conversation with OpenAI's voice model on, minted by the service for this recruiter
// (services/bff/src/voice.ts). Neither a query nor a mutation: nothing is cached and nothing
// refetched, because each Talk needs a fresh key — one opens one call, for ten minutes.
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { VoiceLookupResponse, VoiceSessionResponse } from '@vera/bff';
import { API_PREFIX, type RequestFn } from './core.ts';
import { KEYS } from './mutation.ts';
import { useRequest } from './provider.tsx';

/**
 * What her voice's lookup asks (23 Sep 2026): which kind — a person's fact, a Search's people, or
 * who is waiting (24 Sep) — whose, which facts, the words, and the chat it goes to.
 */
export interface VoiceLookupBody {
  kind: 'person' | 'search' | 'waiting';
  name: string;
  facts: string;
  question: string;
  chatId: string | null;
  searchId: string | null;
}

export const voiceApi = (request: RequestFn) => ({
  session: () => request<VoiceSessionResponse>(`${API_PREFIX}/voice/session`, { method: 'POST' }),
  // Since 23 Sep 2026: a plain fact about one named person, and since 24 Sep who is on a Search or
  // waiting, read from the records; when it answers, the brain API writes the chat's row
  // (services/bff/src/voice-lookup.ts).
  lookUp: (body: VoiceLookupBody) =>
    request<VoiceLookupResponse>(`${API_PREFIX}/voice/lookup`, { method: 'POST', body }),
});

/**
 * The lookup as the kadmon panel calls it: when it wrote the chat's row, the chat is refetched, as
 * after a find (api/find.ts `useFindStreaming`), so the answer and its people's cards are on screen
 * — where an email is, since her voice never reads one out.
 */
export function useVoiceLookup(): (body: VoiceLookupBody) => Promise<VoiceLookupResponse> {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useCallback(
    async (body) => {
      const response = await voiceApi(request).lookUp(body);
      if (response.found === 'answer') {
        await Promise.all(KEYS.chat.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
      }
      return response;
    },
    [request, queryClient],
  );
}
